import React, { useEffect, useState } from "react";
import api from "../api/axios";
import { useNavigate } from "react-router-dom";
import "./AdminUserList.css";

function AdminUserList() {
  const [users, setUsers] = useState([]);
  const [message, setMessage] = useState("");
  const navigate = useNavigate();

  const fetchUsers = async () => {
    try {
      const res = await api.get("/api/admin/users");
      setUsers(res.data || []);
    } catch (err) {
      console.error(err);
      if (err?.response?.status === 401 || err?.response?.status === 403) {
        setMessage("セッションが無効です。再ログインしてください。");
        navigate("/admin/login");
        return;
      }
      setMessage("ユーザー取得に失敗しました");
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("adminToken");
    navigate("/admin/login");
  };

  useEffect(() => {
    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleToggleActive = async (id, isActive) => {
    try {
      await api.patch(`/api/admin/user/${id}/activate`, {});
      setMessage("ステータスを更新しました");
      fetchUsers();
    } catch (err) {
      console.error(err);
      if (err?.response?.status === 401 || err?.response?.status === 403) {
        navigate("/admin/login");
        return;
      }
      setMessage("更新に失敗しました");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("このユーザーを削除しますか？")) return;
    try {
      await api.delete(`/api/admin/user/${id}`);
      setMessage("削除しました");
      fetchUsers();
    } catch (err) {
      console.error(err);
      if (err?.response?.status === 401 || err?.response?.status === 403) {
        navigate("/admin/login");
        return;
      }
      setMessage("削除に失敗しました");
    }
  };

  // ★ 追加：Amazon連携解除
  const handleUnlinkAmazon = async (id) => {
    if (!window.confirm("このユーザーのAmazon連携を解除しますか？")) return;
    try {
      await api.patch(`/api/admin/user/${id}/unlink-amazon`, {});
      setMessage("Amazon連携を解除しました");
      fetchUsers();
    } catch (err) {
      console.error(err);
      if (err?.response?.status === 401 || err?.response?.status === 403) {
        navigate("/admin/login");
        return;
      }
      setMessage("連携解除に失敗しました");
    }
  };

  // ----- 表示ユーティリティ -----
  const truncate = (str, max) => {
    if (!str) return "";
    return str.length > max ? `${str.slice(0, max)}…` : str;
  };
  const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("ja-JP") : "-");

  // Stripe列の表示（既存）
  const getStripeView = (u) => {
    const status = (u.stripeStatus || u.subscriptionStatus || "").toLowerCase();
    const isRegistered = ["active", "trialing"].includes(status);
    const isWarn = ["past_due", "unpaid", "incomplete", "incomplete_expired"].includes(status);
    const isCanceled = status === "canceled";

    let nextStop = u.cancelAt || null;
    if (!nextStop) {
      if (status === "trialing") nextStop = u.trialEndAt || null;
      else if (["active", "past_due", "unpaid"].includes(status)) nextStop = u.periodEndAt || null;
    }

    let color = "black";
    if (isRegistered) color = "green";
    else if (isWarn) color = "#a97700";
    else if (isCanceled) color = "red";

    const label = isRegistered ? "登録済み" : "未登録";
    return { label, color, raw: status || "unknown", nextStop };
  };

  return (
    <div style={{ maxWidth: 1300, margin: "0 auto", padding: 20 }}>
      <h2>ユーザー管理</h2>
      {message && <p>{message}</p>}

      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 10 }}>
        <button onClick={handleLogout} className="logout-button">ログアウト</button>
      </div>

      <table border="1" cellPadding="8" cellSpacing="0" width="100%">
        <thead>
          <tr>
            <th>メール</th>
            <th>氏名</th>
            <th>作成日</th>
            <th>Profile ID</th>
            <th>SKD数</th>
            <th>Stripe</th>
            <th>停止予定</th>
            <th>有効</th>
            <th>操作</th>
          </tr>
        </thead>
        <tbody>
          {users.map((u) => {
            const isDuplicateProfileId =
              u.amazonProfileId &&
              users.filter((x) => x.amazonProfileId === u.amazonProfileId).length > 1;

            const stripeView = getStripeView(u);

            return (
              <tr key={u._id}>
                {/* メール：35文字で省略、titleに全文 */}
                <td title={u.email}>{truncate(u.email, 35)}</td>

                {/* 氏名：リンク時も中身は省略、titleに全文 */}
                <td title={u.name}>
                  {u.amazonSellerId ? (
                    <a
                      href={`https://www.amazon.co.jp/s?i=merchant-items&me=${u.amazonSellerId}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: "blue", textDecoration: "underline" }}
                    >
                      {truncate(u.name, 15)}
                    </a>
                  ) : (
                    truncate(u.name, 15)
                  )}
                </td>

                {/* 作成日：年月日のみ */}
                <td style={{ textAlign: "center" }}>{fmtDate(u.createdAt)}</td>

                {/* Profile ID + 解除ボタン（IDがあるときのみ） */}
                <td style={{ textAlign: "center", color: isDuplicateProfileId ? "red" : "black", whiteSpace: "nowrap" }}>
                  {u.amazonProfileId || "-"}
                  {u.amazonProfileId ? (
                    <button
                      onClick={() => handleUnlinkAmazon(u._id)}
                      style={{
                        marginLeft: 8,
                        padding: "2px 6px",
                        fontSize: 12,
                        cursor: "pointer",
                      }}
                      title="Amazon連携を解除"
                    >
                      解除
                    </button>
                  ) : null}
                </td>

                <td style={{ textAlign: "center" }}>{u.scheduleCount}</td>

                {/* Stripe 列 */}
                <td
                  style={{ textAlign: "center", color: stripeView.color }}
                  title={`Stripe status: ${stripeView.raw}`}
                >
                  <span
                    style={{
                      padding: "2px 8px",
                      borderRadius: 9999,
                      border: `1px solid ${stripeView.color}`,
                      display: "inline-block",
                      fontSize: 12,
                    }}
                  >
                    {stripeView.label}
                  </span>
                </td>

                {/* 停止予定：年月日のみ */}
                <td style={{ textAlign: "center" }}>{fmtDate(stripeView.nextStop)}</td>

                <td style={{ textAlign: "center" }}>{u.isActive ? "有効" : "無効"}</td>
                <td style={{ textAlign: "center", whiteSpace: "nowrap" }}>
                  <button onClick={() => handleToggleActive(u._id, u.isActive)}>
                    {u.isActive ? "無効にする" : "有効にする"}
                  </button>
                  <button
                    onClick={() => handleDelete(u._id)}
                    style={{ marginLeft: "5px", color: "red" }}
                  >
                    削除
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export default AdminUserList;